import {
  ProductInventoryDoc,
  ProductOutboundOperation,
  ProductOutboundTransactionDoc,
  ProductTransactionStatus,
} from '@gooduncles/gu-app-schema';
import { ColDef, ITooltipParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Empty, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';

import {
  getCellStyleForTransactionStatus,
  linkCellStyle,
  outboundReferenceCollectionFormatter,
  productOutboundOperationFormatter,
  productTransactionStatusFormatter,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { datesToStringDateWhere, getDefaultDates } from 'src/utils/date-util';

import useOutboundTxnsForInventory from 'src/hooks/subscription/useOutboundTxnsForInventory';
import useOrderById from 'src/hooks/swr/useOrderById';
import useSWRInventoryIssue from 'src/hooks/swr/useSWRInventoryIssue';
import useSWRProductSupplierInventoryReceipt from 'src/hooks/swr/useSWRProductSupplierInventoryReceipt';
import useSWRProductSupplierPurchaseOrders from 'src/hooks/swr/useSWRProductSupplierPurchaseOrders';
import useSWRStoreIssue from 'src/hooks/swr/useSWRStoreIssue';
import useDateForTableOptions from 'src/hooks/useDateForTableOptions';

import TableOptions from 'src/components/Common/TableOptions/TableOptions';

import OrderView from '../OrderView/OrderView';
import ProductSupplierPurchaseOrderView from '../ProductSupplierPurchaseOrder/ProductSupplierPurchaseOrderView';
import InventoryIssueView from './InventoryIssueView';
import InventoryStoreIssueView from './InventoryStoreIssueView';
import ProductSupplierReceiptView from './ProductSupplierReceiptView';
import withTransactionData from './withTransactionData';

const defaultValue = getDefaultDates(3, 0);

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface InventoryPurchaseOutboundHistoryPanelProps {
  inventory: ProductInventoryDoc | undefined;
}

/**
 * 재고 출고(발주) 내역 패널
 * @param param0
 * @returns
 */
const InventoryPurchaseOutboundHistoryPanel: FC<InventoryPurchaseOutboundHistoryPanelProps> = ({ inventory }) => {
  const { dates, setDates, leftDate, rightDate } = useDateForTableOptions(datesToStringDateWhere, defaultValue);
  const { outboundTransactions, error } = useOutboundTxnsForInventory(inventory?._id ?? null, leftDate, rightDate);
  const [selectedTransaction, setSelectedTransaction] = useState<ProductOutboundTransactionDoc | null>(null);
  const [selectedPath, setSelectedPath] = useState<'operation' | 'referenceCollection' | null>(null);

  const columnDefs: ColDef<ProductOutboundTransactionDoc>[] = useMemo(
    () => [
      {
        headerName: 'Id',
        field: '_id',
        width: 80,
        hide: true,
      },
      {
        headerName: '요청일',
        field: 'requestDate',
        width: 140,
      },
      {
        headerName: '출고 방식',
        field: 'operation',
        width: 120,
        cellStyle: (params: any) => {
          if (params.data?.operation === ProductOutboundOperation.INVENTORY) {
            return {};
          }
          return linkCellStyle;
        },
        valueFormatter: productOutboundOperationFormatter,
        filterParams: {
          valueFormatter: productOutboundOperationFormatter,
        },
        onCellClicked: (params: any) => {
          const data = params.data as ProductOutboundTransactionDoc;
          if (data.operation === ProductOutboundOperation.INVENTORY) {
            return;
          }
          setSelectedTransaction(data);
          setSelectedPath('operation');
        },
      },
      {
        headerName: '출고 경로',
        field: 'referenceCollection',
        width: 120,
        cellStyle: linkCellStyle,
        valueFormatter: outboundReferenceCollectionFormatter,
        filterParams: {
          valueFormatter: outboundReferenceCollectionFormatter,
        },
        onCellClicked: (params: any) => {
          const data = params.data as ProductOutboundTransactionDoc;
          setSelectedTransaction(data);
          setSelectedPath('referenceCollection');
        },
      },
      {
        headerName: '변경량',
        field: 'requestQuantity',
        width: 100,
        type: 'numericColumn',
        cellRenderer: (params: any) => {
          const formattedValue = params.value === Infinity ? '∞' : formatNumber(params.value);
          const status: ProductTransactionStatus = params.data?.status;
          return <span className={`stock-cell outbound ${status}`}>{formattedValue}</span>;
        },
      },
      {
        headerName: '상태',
        field: 'status',
        width: 80,
        valueFormatter: productTransactionStatusFormatter,
        cellStyle: getCellStyleForTransactionStatus,
        filterParams: {
          valueFormatter: productTransactionStatusFormatter,
        },
        tooltipValueGetter: (params: ITooltipParams<ProductOutboundTransactionDoc, ProductTransactionStatus>) => {
          if (
            params.value &&
            [ProductTransactionStatus.REJECTED, ProductTransactionStatus.FAILED].includes(params.value)
          ) {
            return params.data?.error;
          }
          return null;
        },
      },
    ],
    []
  );

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  if (!inventory) {
    return <Empty description='재고 정보가 없습니다.' />;
  }

  return (
    <div className='height100 flexColumn'>
      <TableOptions setDates={setDates} defaultValue={dates} />
      <div className='flexRow flex1 overflowHidden'>
        <div className='ag-theme-alpine flex1'>
          <AgGridReact rowData={outboundTransactions} columnDefs={columnDefs} defaultColDef={defaultColDef} />
        </div>
        <div className='flex1 overflowHidden'>
          {selectedTransaction && (
            <>
              {selectedPath === 'operation' && selectedTransaction.operation === ProductOutboundOperation.PURCHASE && (
                <ProductSupplierPurchaseOrderViewForTransaction transaction={selectedTransaction} />
              )}
              {selectedPath === 'referenceCollection' && (
                <>
                  {selectedTransaction.referenceCollection === 'inventoryIssue' && (
                    <InventoryIssueViewForTransaction transaction={selectedTransaction} />
                  )}
                  {selectedTransaction.referenceCollection === 'productSupplier' && (
                    <ProductSupplierReceiptViewForTransaction transaction={selectedTransaction} />
                  )}
                  {selectedTransaction.referenceCollection === 'order' && (
                    <div className='borderedScrollBox'>
                      <OrderViewForTransaction transaction={selectedTransaction} />
                    </div>
                  )}
                  {selectedTransaction.referenceCollection === 'storeIssue' && (
                    <InventoryStoreIssueViewForTransaction transaction={selectedTransaction} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductSupplierPurchaseOrderViewForTransaction = withTransactionData<{ productSupplierPurchaseOrders: any }>(
  ProductSupplierPurchaseOrderView,
  (transaction) => {
    const { productSupplierPurchaseOrders } = useSWRProductSupplierPurchaseOrders([
      ['outboundTransactionIds', 'array-contains', transaction._id],
    ]);
    return { data: productSupplierPurchaseOrders ? { productSupplierPurchaseOrders } : null };
  },
  '거래처 발주 내역이 없습니다.'
);

const InventoryIssueViewForTransaction = withTransactionData<{ inventoryIssue: any }>(
  InventoryIssueView,
  (transaction) => {
    const { inventoryIssue } = useSWRInventoryIssue(transaction.referenceId);
    return { data: inventoryIssue ? { inventoryIssue } : null };
  },
  '재고 이슈 정보가 없습니다.'
);

const InventoryStoreIssueViewForTransaction = withTransactionData<{ storeIssue: any }>(
  InventoryStoreIssueView,
  (transaction) => {
    const { storeIssue } = useSWRStoreIssue(transaction.referenceId);
    return { data: storeIssue ? { storeIssue } : null };
  },
  '거래처 입고 정보가 없습니다.'
);
const ProductSupplierReceiptViewForTransaction = withTransactionData<{ supplierReceipt: any }>(
  ProductSupplierReceiptView,
  (transaction) => {
    const { supplierInventoryReceipt } = useSWRProductSupplierInventoryReceipt(transaction.referenceId);
    return { data: supplierInventoryReceipt ? { supplierReceipt: supplierInventoryReceipt } : null };
  },
  '거래처 입고 정보가 없습니다.'
);

const OrderViewForTransaction = withTransactionData<{ order: any }>(
  OrderView,
  (transaction) => {
    const { order } = useOrderById(transaction.referenceId);
    return { data: order ? { order } : null };
  },
  '주문 정보가 없습니다.'
);

export default InventoryPurchaseOutboundHistoryPanel;
